import React from 'react';
import { AlertTriangle, Sprout } from 'lucide-react';
import type {
  CohortFigures,
  CohortWindowFigures,
  MilestoneFigure,
  WindowSelection,
} from '../../../hooks/pipelineTracker/usePipelineReport';
import { money, num } from './pipelineParts';
import { CHART_PALETTE, HousePanel, HouseStatCard, HouseStatGrid } from './trackerKit';
import { PeriodChip } from './PeriodBand';

/**
 * What became of the deals that STARTED in this window.
 *
 * WHY THIS EXISTS ALONGSIDE THE FLOW BAND. The flow band counts events off date
 * columns, so it cannot see a commercial deal: Salestrekker writes deal statuses
 * through ApplyOnline, which is residential only, and a commercial deal reaches
 * settlement carrying nothing but Date Created and Date Settled. Measured at
 * Evoke, roughly half of one week's twelve lodgements were commercial and only
 * five showed. This block asks the question the dates cannot answer, by taking
 * the period from Date Created (which every deal has) and the outcome from the
 * deal's current stage (which every deal also has).
 *
 * DERIVED IS LABELLED AS DERIVED. Where a figure was established by a stage
 * rather than a date, the count says so. A number that mixes measured and
 * inferred evidence without declaring it is the same failure as an interpolated
 * one, only quieter, and interpolation is precisely what makes the tenant's own
 * hand-built processing times unreliable.
 *
 * CENSORING IS STATED, NOT HIDDEN. Every window ends at the snapshot, so a short
 * one is censored by construction: deals created this week have had days, not
 * months, to reach a lender. Saying so is the difference between a reader seeing
 * a young cohort and a reader seeing a collapse.
 */

const pct = (bp: number | null): string => (bp === null ? '--' : `${(bp / 100).toFixed(0)}%`);

/**
 * Below this the window is too young for its figures to mean much.
 *
 * Exported because the per-broker spread in PeopleTable reads the same cohort and must carry the
 * same warning. Two copies of this number would let one surface warn and the other not, over the
 * identical set of deals.
 */
export const CENSORED_BELOW_DAYS = 60;

/**
 * Which cohort the selection is asking for, in one place.
 *
 * Exported for the same reason CENSORED_BELOW_DAYS is: the per-broker spread in PeopleTable
 * describes the same deals this band just described, and two copies of the lookup would let
 * one surface follow the selection while the other lagged it. The custom case is the one that
 * diverges quietly, because a window chosen but not yet applied has no figures at all, and a
 * second implementation could plausibly fall back to a rolling window instead of returning
 * undefined. Returning undefined is what lets each caller decide how to say "not yet".
 */
export function resolveCohortWindow(
  cohorts: readonly CohortFigures[] | undefined,
  selected: WindowSelection,
  custom?: CohortWindowFigures,
): CohortWindowFigures | undefined {
  if (selected === 'custom') return custom;
  return (cohorts ?? []).find((c) => c.period === selected);
}

const Evidence: React.FC<{ figure: MilestoneFigure }> = ({ figure }) => {
  if (figure.count === 0 || figure.fromStage === 0) return null;
  return (
    <span className="block text-[0.6875rem] leading-tight text-gray-500">
      {num(figure.fromDate)} by date, {num(figure.fromStage)} by stage
    </span>
  );
};

const TITLE = 'What became of the deals created in this window';

export interface CohortBandProps {
  cohorts: CohortFigures[];
  selected: WindowSelection;
  /** The applied custom window, shown when it is the selection. */
  custom?: CohortWindowFigures;
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
}

const CohortBand: React.FC<CohortBandProps> = ({ cohorts, selected, custom, accent }) => {
  // A custom window is censored on exactly the same rule as a rolling one, because the censor
  // reads `windowDays` and the server computes that for every window in one place. A fortnight
  // chosen by hand therefore carries the same warning WTD does, rather than reading as a
  // collapse in conversion to anyone who picked a short range.
  const c = resolveCohortWindow(cohorts, selected, custom);

  // A PROMPT, not a fallback, and the difference is what each band puts on screen. The period
  // band can fall back to a rolling window between choosing Custom and pressing Apply because
  // it prints the window it is showing ("week to date", with both dates); this band prints no
  // window at all, so rolling figures here would be a financial-year cohort sitting under a
  // CUSTOM button with nothing to say which deals it counted. Vanishing is no better: the
  // block simply disappears mid-task with no hint it is waiting on the dates above.
  if (!c && selected === 'custom') {
    return (
      <HousePanel title={TITLE} icon={Sprout} color={accent}>
        <p className="text-xs text-gray-400">
          Choose a From and To date above and apply them to see this.
        </p>
      </HousePanel>
    );
  }

  if (!c) return null;

  const censored = c.windowDays < CENSORED_BELOW_DAYS;

  const cells: Array<{ label: string; value: string; sub: string; figure?: MilestoneFigure; colour: string }> = [
    {
      label: 'Created',
      value: num(c.cohortDeals),
      sub: money(c.cohortValueCents),
      colour: CHART_PALETTE[0],
    },
    // Meeting booked and Approved carry a money sub-line rather than a share of created,
    // because neither is a conversion of the cell beside it: these are attainment counts on one
    // entry cohort, and a percentage between two of them would read as a funnel step.
    ...(c.reachedMeeting
      ? [
          {
            label: 'Meeting booked',
            value: num(c.reachedMeeting.count),
            sub: money(c.reachedMeeting.valueCents),
            figure: c.reachedMeeting,
            colour: CHART_PALETTE[5],
          },
        ]
      : []),
    {
      label: 'Reached lender',
      value: num(c.reachedLodged.count),
      sub: `${pct(c.lodgedPerCreatedBp)} of created`,
      figure: c.reachedLodged,
      colour: CHART_PALETTE[1],
    },
    ...(c.reachedApproved
      ? [
          {
            label: 'Approved',
            value: num(c.reachedApproved.count),
            sub: money(c.reachedApproved.valueCents),
            figure: c.reachedApproved,
            colour: CHART_PALETTE[3],
          },
        ]
      : []),
    {
      label: 'Settled',
      value: num(c.settled.count),
      sub: `${pct(c.settledPerLodgedBp)} of lodged · ${pct(c.settledPerCreatedBp)} of created`,
      figure: c.settled,
      colour: CHART_PALETTE[2],
    },
    {
      label: 'Lost',
      value: num(c.lost.count),
      sub: money(c.lost.valueCents),
      figure: c.lost,
      colour: CHART_PALETTE[4],
    },
  ];

  // Four to six cards (Meeting booked and Approved are absent from an older payload), each laid out
  // by the house grid for that count so no row leaves an orphan.
  const gridCount = Math.min(6, Math.max(3, cells.length)) as 3 | 4 | 5 | 6;
  const period = selected === 'custom' ? 'custom' : selected;

  return (
    <HousePanel
      title={TITLE}
      icon={Sprout}
      color={accent}
      badge={<PeriodChip period={period} from={c.from} to={c.to} />}
      blurb="Counted from the stage each deal sits in today, so commercial deals are included."
    >
      <HouseStatGrid count={gridCount} testId="cohort-row">
        {cells.map((cell) => (
          <HouseStatCard
            key={cell.label}
            label={cell.label}
            color={cell.colour}
            valueColor={cell.colour}
            value={<span className="tabular-nums">{cell.value}</span>}
            footer={
              <>
                <span className="block tabular-nums">{cell.sub}</span>
                {cell.figure && <Evidence figure={cell.figure} />}
              </>
            }
            testId={`cohort-${cell.label.toLowerCase().replace(/\s+/g, '-')}`}
          />
        ))}
      </HouseStatGrid>

      {censored && (
        <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-amber-300/90">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
          <span>
            This window covers {num(c.windowDays)} days. Deals created inside it have not had time to
            reach a lender or settle, so these rates read low by construction. Compare a full year
            before reading anything into them.
          </span>
        </p>
      )}

      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        A deal belongs to the window it was <em>created</em> in, both dates included, not the one it settled in, so a rate
        here answers "of the deals we started then, how many converted". Nothing is estimated: where
        a milestone date is missing the deal is counted from its stage, and where neither exists it
        is not counted at all.
      </p>
    </HousePanel>
  );
};

export default CohortBand;
